<template>
    <div :class="inline ? 'tile-content no-padding' : 'tile'">
        <div class="tile-content">
            <div class="grid-content">
                <div class="grid-item grid-item-full">
                    <v-checkbox
                        label="Match all cases"
                        :value="true"
                        v-model="isMatchAll"
                        @input="resetCriteria"
                        :caption="matchAllCaption"
                    />
                </div>
            </div>
        </div>
        <div
            v-if="!isMatchAll"
            class="tile-content"
            style="position: relative">
            <v-inline-loader v-if="isLoading"/>
            <div class="divider divider-hidden" v-if="isLoading"></div>
            <div v-else>
                <criteria-input
                    :field-provider="fieldProvider"
                    @select-field="selectField"
                    :class="{'stack-m': normalizedCriteria.hasConditions}"/>
                <condition-list
                    :conditions="conditionList"
                    @update-condition="updateCondition"
                    @remove-condition="removeCondition"
                    :field-provider="fieldProvider"/>
            </div>
        </div>
    </div>
</template>

<script>
    import EventModel from '@/common/models/rules/event-model';
    import {vCheckbox} from '@/components/form/index';
    import vInlineLoader from '@/components/v-inline-loader';
    import CriteriaInput from '@/modules/rules-engine/components/condition-builder/criteria-input';
    import ConditionList from '@/modules/rules-engine/components/condition-builder/condition-list';
    import getFieldsFromPathways, {extractCustomFieldResources}
        from '@/modules/rules-engine/components/condition-builder/get-fields-from-pathways';
    import resolveFilterType from '@/components/query/helpers/resolve-filter-type';
    import NormalizedCriteria from '@/components/query/adapters/criteria/normalized-criteria';
    import FieldProvider from '@/modules/rules-engine/components/condition-builder/field-provider';

    export default {
        name: 'condition-builder',
        components: {
            vCheckbox,
            vInlineLoader,
            CriteriaInput,
            ConditionList,
        },
        props: {
            inline: {
                type: Boolean,
                default: false,
            },
            criteria: Object,
            event: {
                type: EventModel,
            },
            ruleType: {
                type: String,
                default: 'rule',
            },
        },
        computed: {
            customFieldResources() {
                return extractCustomFieldResources(this.event.pathways);
            },
            conditionList() {
                return this.normalizedCriteria.getConditions();
            },
            matchAllCaption() {
                return `When enabled, this ${this.ruleType} will always be triggered by the event '${this.event.title}'.`;
            },
        },
        data() {
            return {
                isLoading: true,
                criteriaSet: null,
                /**
                 * @type {NormalizedCriteria}
                 */
                normalizedCriteria: null,
                fieldProvider: null,
                isMatchAll: this.criteria === null,
            };
        },
        watch: {
            criteria(newValue, oldValue) {
                this.isMatchAll = newValue === null;
                if (!oldValue && newValue) {
                    this.initHandlers();
                }
            },
        },
        methods: {
            async preloadCustomFields() {
                this.isLoading = true;
                const fetches = this.customFieldResources
                    .map(resource => this.$store.dispatch('rulesEngine/fetchCustomFields', resource));
                await Promise.all(fetches);
                this.initHandlers();
                this.isLoading = false;
            },
            initHandlers() {
                // the provider must init after the custom field info is loaded and before
                // the normalization happens
                this.fieldProvider = new FieldProvider({
                    pathwayResolver: getFieldsFromPathways,
                    typeResolver: resolveFilterType,
                    pathways: this.event.pathways,
                    customFieldsStore: this.$store.getters['rulesEngine/customFields'],
                });
                this.normalizedCriteria = new NormalizedCriteria(this.criteria, this.fieldProvider);
            },
            resetCriteria() {
                this.$emit('update', null);
            },
            updateCondition({index, condition}) {
                this.normalizedCriteria.updateAtIndex(index, condition);
                this.$emit('update', this.normalizedCriteria.reconstructCriteria());
            },
            removeCondition(index) {
                this.normalizedCriteria.removeAtIndex(index);
                if (!this.normalizedCriteria.hasConditions) {
                    this.resetCriteria();
                } else {
                    this.$emit('update', this.normalizedCriteria.reconstructCriteria());
                }
            },
            selectField(data) {
                if (data) {
                    this.normalizedCriteria.addField(data);
                    this.$emit('update', this.normalizedCriteria.reconstructCriteria());
                } else {
                    // TODO handle no matching field info
                }
            },
        },
        async created() {
            await this.preloadCustomFields();
        },
    };
</script>
